import {
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type FormEvent,
  type ReactNode
} from "react";
import { Link } from "react-router-dom";
import { PRODUCT } from "@word-duel/shared/config";

export function AppShell({
  children,
  compact = false,
  headerContent,
  playMode = false
}: {
  children: ReactNode;
  compact?: boolean;
  headerContent?: ReactNode;
  playMode?: boolean;
}) {
  const [online, setOnline] = useState(() => navigator.onLine);
  const [brandLead, brandTail = ""] = PRODUCT.name.split("/");

  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  return (
    <div
      className={`app-shell${compact ? " app-shell--compact" : ""}${playMode ? " app-shell--play" : ""}`}
    >
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <header className={`site-header ${headerContent ? "site-header--game" : ""}`}>
        {headerContent ?? (
          <>
            <Link className="wordmark" to="/" aria-label={`${PRODUCT.name} home`}>
              <span aria-hidden="true">{brandLead}</span>
              {brandTail && (
                <>
                  <span className="wordmark__slash" aria-hidden="true">
                    /
                  </span>
                  <span aria-hidden="true">{brandTail}</span>
                </>
              )}
            </Link>
            <div className="site-header__tools">
              <span
                className={`network-badge ${online ? "is-online" : "is-offline"}`}
                role="status"
              >
                <span aria-hidden="true">{online ? "●" : "×"}</span> {online ? "Online" : "Offline"}
              </span>
              <Link className="header-link" to="/">
                My games
              </Link>
            </div>
          </>
        )}
      </header>
      <div id="main-content">{children}</div>
      <footer className="site-footer">
        <span>{PRODUCT.name}</span>
        <span>Private games. No account required.</span>
      </footer>
    </div>
  );
}

export function Button({
  className = "",
  variant = "default",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "default" | "primary" | "danger" | "quiet";
}) {
  return <button className={`button button--${variant} ${className}`.trim()} {...props} />;
}

export function Field({
  label,
  hint,
  error,
  children
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      {children}
      {hint && <span className="field__hint">{hint}</span>}
      {error && (
        <span className="field__error" role="alert">
          {error}
        </span>
      )}
    </label>
  );
}

export function Alert({
  tone = "info",
  children
}: {
  tone?: "info" | "error" | "success" | "warning";
  children: ReactNode;
}) {
  return (
    <div className={`alert alert--${tone}`} role={tone === "error" ? "alert" : "status"}>
      {children}
    </div>
  );
}

export function Modal({
  title,
  description,
  onClose,
  children,
  wide = false,
  id,
  className = "",
  eyebrow = "ACTION / CONFIRM"
}: {
  title: string;
  description?: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
  id?: string;
  className?: string;
  eyebrow?: string;
}) {
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = dialogRef.current;
    dialog?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "Tab" && dialog) {
        const focusable = [
          ...dialog.querySelectorAll<HTMLElement>(
            'a[href], button:not(:disabled), input:not(:disabled), summary, [tabindex]:not([tabindex="-1"])'
          )
        ].filter((element) => !element.hasAttribute("hidden"));
        const first = focusable[0];
        const last = focusable.at(-1);
        if (!first || !last) {
          event.preventDefault();
        } else if (
          event.shiftKey &&
          (document.activeElement === first || document.activeElement === dialog)
        ) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      previous?.focus();
    };
  }, [onClose]);

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <section
        id={id}
        ref={dialogRef}
        className={`modal${wide ? " modal--wide" : ""}${className ? ` ${className}` : ""}`}
        role="dialog"
        tabIndex={-1}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
      >
        <div className="modal__header">
          <div>
            <p className="eyebrow">{eyebrow}</p>
            <h2 id={titleId}>{title}</h2>
            {description && <p id={descriptionId}>{description}</p>}
          </div>
          <Button
            variant="quiet"
            className="icon-button"
            onClick={onClose}
            aria-label="Close dialog"
          >
            ×
          </Button>
        </div>
        {children}
      </section>
    </div>
  );
}

export function ConfirmDialog({
  title,
  description,
  confirmLabel,
  danger = false,
  busy = false,
  onConfirm,
  onClose
}: {
  title: string;
  description: string;
  confirmLabel: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
}) {
  const submit = (event: FormEvent) => {
    event.preventDefault();
    void onConfirm();
  };
  return (
    <Modal title={title} description={description} onClose={onClose}>
      <form onSubmit={submit} className="modal__actions">
        <Button type="button" onClick={onClose} disabled={busy}>
          Keep playing
        </Button>
        <Button
          type="submit"
          variant={danger ? "danger" : "primary"}
          disabled={busy}
          data-testid={`confirm-${confirmLabel.toLowerCase()}`}
        >
          {busy ? "Working…" : confirmLabel}
        </Button>
      </form>
    </Modal>
  );
}

export function Spinner({ label = "Loading" }: { label?: string }) {
  return (
    <div className="loader" role="status">
      <span className="loader__mark" aria-hidden="true">
        W
      </span>
      <strong>{label}</strong>
    </div>
  );
}
